// Toast copy for the clinical-task write cohort (ARCFE-1082, meta v1.1).
//
// WHY THIS FILE EXISTS AT ALL. Three delegates in `src/hooks/mutation/` held
// nothing but a `toast(...)`. Under meta v1.0 they could not dissolve, because
// two of them resolve their words through `react-intl` and v1.0's `meta`
// carried only a plain string. v1.1 accepts a `MessageDescriptor`, so the copy
// travels with the mutation as inert data and all three delegates become pure
// re-export shims.
//
// THE IDS ARE MOVED, NOT MINTED. `JA0qQV741V`, `7hZhjDJWAD`, `UcpdI6n3b/` and
// `qgg3bQzoON` are transcribed VERBATIM from the delegates that held them
// (which transcribed them from the pre-carve hooks). An id is what ties a
// string to its Crowdin translations; re-hashing one silently ships
// untranslated copy to every non-English user. Changing a `defaultMessage`
// here is a user-visible change, not a tidy.
//
// WHY `defineMessages` AND NOT A PLAIN OBJECT LITERAL. The formatjs extractor
// recognises `defineMessages` / `defineMessage` / `formatMessage` call sites,
// not object literals that happen to have an `id`. A plain literal would type-
// check, render correctly in English, and then be DROPPED from the catalog on
// the next `hyperlocalise extract` — deleting four existing translations
// without a single failing check. The `.messages.ts` basename is the repo's
// convention for extractable copy and is what the localisation workflow's
// pull-request path filter watches (`.github/workflows/i18n-pipeline.yml`).
//
// WHY THIS IS LEGAL INSIDE `/data`. `react-intl` is not a toaster and not the
// design system: `defineMessages` is an identity function over data, nothing
// here renders, and no `IntlShape` is read. The rendering half stays app-side
// in `src/utils/reactQueryClient.ts`, which formats these through
// `formatMessageOutsideReact`. The package's own `manifest-boundary.test.ts`
// asserts the property that matters — `/data` mentions no design system and
// calls no `toast(...)` — rather than trusting this comment.
import type { MutationToastMeta } from '@heidi/api/meta';
import { defineMessages } from 'react-intl';

const messages = defineMessages({
  assignErrorTitle: {
    defaultMessage: 'Error assigning task',
    id: 'JA0qQV741V',
  },
  assignErrorDescription: {
    defaultMessage: 'Could not assign the task. Please try again.',
    id: '7hZhjDJWAD',
  },
  unassignErrorTitle: {
    defaultMessage: 'Error unassigning task',
    id: 'UcpdI6n3b/',
  },
  unassignErrorDescription: {
    defaultMessage: 'Could not unassign the task. Please try again.',
    id: 'qgg3bQzoON',
  },
});

/**
 * One entry per hook, held as a named constant rather than inlined at each
 * `useMutation` so the copy-fidelity suite has a single object to assert
 * against and a reviewer sees every user-visible string of this family in one
 * place.
 *
 * The `satisfies` (not `:`) keeps the literal types, so a typo in a key name
 * fails HERE rather than silently producing a mutation that declares no toast
 * and quietly stops toasting — the exact failure this mechanism makes possible.
 *
 * RESTORE IS DELIBERATELY NOT INTL. Its two strings were hardcoded English at
 * the delegate and at the pre-carve hook (P0.2 recorded them as 2 of that
 * family's 6 un-intl'd sites). Converting them to descriptors would mint two
 * new ids with no translations behind them — a user-visible change smuggled
 * into a move. Localising them is a separate, reviewable decision.
 */
export const CLINICAL_TASK_TOASTS = {
  assign: {
    errorToast: {
      title: messages.assignErrorTitle,
      description: messages.assignErrorDescription,
    },
  },
  unassign: {
    errorToast: {
      title: messages.unassignErrorTitle,
      description: messages.unassignErrorDescription,
    },
  },
  restore: {
    errorToast: {
      title: 'Error restoring task',
      description: 'Could not restore the task. Please try again.',
    },
    successToast: {
      title: 'Task restored',
      description: 'Task has been restored to your active tasks.',
    },
  },
} satisfies Record<string, MutationToastMeta>;
